import { StatePermissions } from './../../../accounts/permissions/model/state-permissions.model';
import { EditWorkflowTransitionPropertiesComponent } from './edit-workflow-transition-properties/edit-workflow-transition-properties.component';
import { TransitionPropertyTypes } from './../../model/transition-property-types.model';
import { TransitionsService } from './../../service/transitions.service';
import { Transitions } from './../../model/transitions.model';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, FormBuilder, Validators } from '@angular/forms';
import { Observable, forkJoin, map, throwError, catchError, of } from 'rxjs';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';
import { WorkflowStateTypes } from '../../model/workflow-state-types.model';
import { Workflows } from '../../model/workflows.model';
import { WorkflowsService } from '../../service/workflows.service';
import { MatTableDataSource } from '@angular/material/table';
import { Roles } from '../../../accounts/roles/model/roles.model';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { UserProfileService } from 'src/app/component/user-profile/service/user-profile.service';
import { StatePermissionsService } from '../../../accounts/permissions/service/state-permissions.service';

@Component({
    selector: 'edit-workflow-transitions',
    templateUrl: './edit-workflow-transitions.component.html',
    styleUrls: ['./edit-workflow-transitions.component.scss']
})
export class EditWorkflowTransitionsComponent implements OnInit {
    @Input() workflow!: Workflows;

    form!: UntypedFormGroup;
    workflowStateTypes: WorkflowStateTypes[];
    transitions: Transitions[];
    roles: Roles[];
    transitionPropertyTypes: TransitionPropertyTypes[];
    statePermissions: StatePermissions[];

    workflowStatesBuilt: any[];

    // Table display
    displayedColumns: string[] = [];

    // Table results
    tableDataSources: MatTableDataSource<any>[] = [];

    isLoading: boolean = false;
    isUpdating: boolean = false;
    msg: MsgModel = {};

    getPropertyPipe = new GetPropertyPipe();

    hasPermission: boolean = false;
    permissionIDs: string[] = ['anthony.nguyen', 'lthiru', 'rdiaz2'];

    constructor(
        private dialog: MatDialog,
        private workflowsService: WorkflowsService,
        private transitionsService: TransitionsService,
        private userProfileService: UserProfileService,
        private statePermissionsService: StatePermissionsService
    ) {}

    ngOnInit(): void {
        this.getAll();
    }

    getAll() {
        this.isLoading = true;

        let obs: Observable<any>[] = [];
        obs.push(this.getTransitions());
        obs.push(this.getWorkflowStateTypes());
        obs.push(this.getWorkflowRoles());
        obs.push(this.indexTransitionPropertyTypes());
        obs.push(this.getPermission());
        obs.push(this.getStatePermissions());

        forkJoin(obs).subscribe((res) => {
            this.updateStateColumns();
            this.buildWorkflowStateTransitions();
            this.updateDataSource();

            this.isLoading = false;
        });
    }

    getPermission() {
        return this.userProfileService.getMyProfile().pipe(
            map((res: any) => {
                if (res.user) {
                    this.hasPermission = this.permissionIDs.findIndex((id: any) => id === res.user.USER_ID) >= 0;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving User Profile.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    getTransitions() {
        return this.workflowsService.getWorkflowTransitions(this.workflow.ID!).pipe(
            map((res) => {
                if (res.transitions) {
                    this.transitions = res.transitions;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving Workflow Transitions.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    getWorkflowStateTypes() {
        return this.workflowsService.getWorkflowStates(this.workflow.ID!).pipe(
            map((res) => {
                if (res.states) {
                    this.workflowStateTypes = res.states;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving Workflow State Types.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    getWorkflowRoles() {
        return this.workflowsService.getWorkflowRoles(this.workflow.ID!).pipe(
            map((res) => {
                if (res.roles) {
                    this.roles = res.roles;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving Workflow Roles.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    indexTransitionPropertyTypes() {
        return this.transitionsService.indexTransitionPropertyTypes().pipe(
            map((res) => {
                if (res.transitionPropertyTypes) {
                    this.transitionPropertyTypes = res.transitionPropertyTypes;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving Workflow Roles.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    getStatePermissions() {
        return this.statePermissionsService.index().pipe(
            map((res) => {
                if (res.statePermissions) {
                    this.statePermissions = res.statePermissions;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving state permissions.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    updateStateColumns() {
        this.displayedColumns = ['NAME'];
        this.workflowStateTypes
            .sort((n1: any, n2: any) => (n1.NAME! > n2.NAME! ? 1 : -1))
            .forEach((wfst) => {
                this.displayedColumns.push(wfst.NAME!);
            });
    }

    buildWorkflowStateTransitions() {
        this.workflowStatesBuilt = [];

        this.workflowStateTypes
            .sort((n1: any, n2: any) => (n1.NAME! > n2.NAME! ? 1 : -1))
            .forEach((wfst: WorkflowStateTypes) => {
                let currentWorkflowStateType = JSON.parse(JSON.stringify(wfst));

                this.workflowStateTypes.forEach((wfstColumn: WorkflowStateTypes) => {
                    currentWorkflowStateType[wfstColumn.ID!] = {
                        WORKFLOW_STATE_TYPES_ID_FROM: wfst.ID!,
                        WORKFLOW_STATE_TYPES_ID_TO: wfstColumn.ID!,
                        ROLES: []
                    };

                    this.roles.forEach((role: Roles) => {
                        let currentRole = {
                            ID: role.ID!,
                            NAME: role.NAME!,
                            transitionID: this.getTransitionID(wfst.ID!, wfstColumn.ID!, role.ID!),
                            hasTransition: this.hasTransition(wfst.ID!, wfstColumn.ID!, role.ID!),
                            hasPermission: this.hasRolePermission(wfst.ID!, role.ID!),
                            transitionPropertyTypes: this.buildTransitionPropertyTypes(wfst.ID!, wfstColumn.ID!, role.ID!)
                        };

                        currentWorkflowStateType[wfstColumn.ID!].ROLES.push(currentRole);
                    });
                });

                this.workflowStatesBuilt.push(currentWorkflowStateType);
            });

        this.workflowStatesBuilt = this.workflowStatesBuilt.sort((n1: any, n2: any) => (n1.NAME! > n2.NAME! ? 1 : -1));
    }

    buildTransitionPropertyTypes(fromStateId: number, toStateId: number, roleId: number) {
        let transitionPropertyTypes: any = [];

        this.transitionPropertyTypes.forEach((tpt) => {
            transitionPropertyTypes.push({
                ID: tpt.ID,
                TYPE: tpt.TYPE,
                hasPropertyType: this.hasTransitionProperty(fromStateId, toStateId, roleId, tpt.ID!)
            });
        });

        return transitionPropertyTypes;
    }

    updateDataSource() {
        this.tableDataSources = [];

        // Data
        let dataSource = new MatTableDataSource<any>();
        dataSource.data = this.workflowStatesBuilt;

        // Paginator
        //dataSource.paginator = this.paginators.toArray()[0];

        // Sort
        // dataSource.sort = this.sorts.toArray()[index];
        // const defaultSort: Sort = { active: 'NAME', direction: 'asc' };
        // dataSource.sort.active = defaultSort.active;
        // dataSource.sort.direction = defaultSort.direction;
        // dataSource.sort.sortChange.emit(defaultSort);
        dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

        // Filter
        dataSource.filterPredicate = (data: any, filter) => {
            const dataStr = JSON.stringify(data).toLowerCase();
            return dataStr.indexOf(filter) != -1;
        };

        this.tableDataSources.push(dataSource);
    }

    toggleTransition(event: any, element: any, roleIndex: number, fromStateId: number, toStateId: number, roleId: number) {
        if (event.checked) {
            this.createTransition(element, roleIndex, fromStateId, toStateId, roleId);
        } else {
            this.deleteTransition(element, roleIndex, fromStateId, toStateId, roleId);
        }
    }

    createTransition(element: any, roleIndex: number, fromStateId: number, toStateId: number, roleId: number) {
        this.transitionsService.create(this.generateTransitionPayload(fromStateId, toStateId, roleId)).subscribe({
            next: (res: any) => {
                if (res.transition) {
                    element[toStateId].ROLES[roleIndex].hasTransition = true;
                    element[toStateId].ROLES[roleIndex].transitionID = res.transition.ID!;
                    this.transitions.push(res.transition);
                } else if (res.msg) {
                    this.msg = res.msg;
                } else {
                    this.msg = { SUCCESS: false, MSG: 'Error creating transition.' };
                }
            },
            error: (err: any) => {
                this.msg = { SUCCESS: false, MSG: 'Error creating transition.' };
            },
            complete: () => {
                //this.buildWorkflowStateTransitions();
                this.updateDataSource();
            }
        });
    }

    deleteTransition(element: any, roleIndex: number, fromStateId: number, toStateId: number, roleId: number) {
        this.transitionsService.deleteTransition(this.generateTransitionPayload(fromStateId, toStateId, roleId)).subscribe({
            next: (res: any) => {
                if (res.msg) {
                    element[toStateId].ROLES[roleIndex].hasTransition = false;
                    element[toStateId].ROLES[roleIndex].transitionID = undefined;
                    element[toStateId].ROLES[roleIndex].transitionPropertyTypes.forEach((transitionPropertyType: any) => {
                        transitionPropertyType.hasPropertyType = false;
                    });

                    let transitionID = this.getTransitionID(fromStateId, toStateId, roleId);
                    let transitionIndex = this.transitions.findIndex((transition: Transitions) => transition.ID === transitionID);
                    if (transitionIndex >= 0) {
                        this.transitions.splice(transitionIndex, 1);
                    }

                    this.msg = res.msg;
                } else {
                    this.msg = { SUCCESS: false, MSG: 'Error deleting transition.' };
                }
            },
            error: (err: any) => {
                this.msg = { SUCCESS: false, MSG: 'Error deleting transition.' };
            },
            complete: () => {
                //this.buildWorkflowStateTransitions();
                this.updateDataSource();
            }
        });
    }

    generateTransitionPayload(fromStateId: number, toStateId: number, roleId: number) {
        let payload: any = {};

        payload.fromState = this.workflowStateTypes.find((wfst: WorkflowStateTypes) => wfst.ID === fromStateId);
        payload.toState = this.workflowStateTypes.find((wfst: WorkflowStateTypes) => wfst.ID === toStateId);
        payload.role = this.roles.find((role: Roles) => role.ID === roleId);

        return payload;
    }

    /*
		Find
	*/
    hasTransition(fromStateId: number, toStateId: number, roleId: number) {
        return (
            this.transitions.findIndex(
                (transition: Transitions) =>
                    transition.WORKFLOW_STATE_TYPES_ID_FROM === fromStateId &&
                    transition.WORKFLOW_STATE_TYPES_ID_TO === toStateId &&
                    transition.ROLES_ID === roleId
            ) >= 0
        );
    }
    hasRolePermission(fromStateId: number, roleId: number) {
        return (
            this.statePermissions.findIndex(
                (statePermission: StatePermissions) => statePermission.WORKFLOW_STATE_TYPES_ID === fromStateId && statePermission.ROLES_ID === roleId
            ) >= 0
        );
    }
    hasTransitionProperty(fromStateId: number, toStateId: number, roleId: number, transitionPropertyTypeId: number) {
        let transition = this.transitions.find(
            (transition: Transitions) =>
                transition.WORKFLOW_STATE_TYPES_ID_FROM === fromStateId && transition.WORKFLOW_STATE_TYPES_ID_TO === toStateId && transition.ROLES_ID === roleId
        );

        if (transition) {
            return (
                transition.TRANSITION_PROPERTIES!.findIndex(
                    (transitionProperty: any) => transitionProperty.TRANSITION_PROPERTY_TYPES_ID === transitionPropertyTypeId
                ) >= 0
            );
        } else {
            return false;
        }
    }
    hasTransitionProperties(role: any) {
        return role.transitionPropertyTypes.findIndex((tpt: any) => tpt.hasPropertyType) >= 0;
    }
    getTransitionID(fromStateId: number, toStateId: number, roleId: number) {
        let transition = this.transitions.find(
            (transition: Transitions) =>
                transition.WORKFLOW_STATE_TYPES_ID_FROM === fromStateId && transition.WORKFLOW_STATE_TYPES_ID_TO === toStateId && transition.ROLES_ID === roleId
        );

        if (transition) {
            return transition.ID;
        } else {
            return undefined;
        }
    }

    // Dialog Modal
    openStudentDialog(fromState: WorkflowStateTypes, toState: WorkflowStateTypes, role: Roles) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            fromState: fromState,
            toState: toState,
            role: role
        };
        dialogConfig.minWidth = '50vw';
        const dialogRef = this.dialog.open(EditWorkflowTransitionPropertiesComponent, dialogConfig);

        dialogRef.afterClosed().subscribe((res: any) => {});
    }
}
